import { ChangeDetectionStrategy, Component, inject, linkedSignal, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { speedDialButtons } from '../../components/layouts/speed-dial-fab/speed-dial-fab.component';
import { BaseComponent } from '../../core/abstractions/base.component';
import { ReportTreeItemModel } from '../../models/core/report-tree-item.model';
import { TreeNodeModel } from '../../models/core/tree-node.model';
import { ReportItemModel } from '../../models/report-item.model';
import { ActionButtonsService } from '../../services/action-buttons.service';
import { ControllerService } from '../../services/controller.service';
import { ReportsService } from '../../services/reports.service';
import { ReportsResource } from '../../services/resources/reports.resource';

@Component({
    selector: 'sl-reports',
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportsComponent extends BaseComponent implements OnInit {
    readonly tree = signal<ReportTreeItemModel[]>([]);
    readonly selectedGroup = linkedSignal(() => this.tree()[0]?.name || '');

    private readonly router = inject(Router);
    private readonly reportsResource = inject(ReportsResource);
    private readonly actionButtonsService = inject(ActionButtonsService);
    private readonly reportsService = inject(ReportsService);
    private readonly controllerService = inject(ControllerService);

    ngOnInit(): void {
        const routerSub = this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                distinctUntilChanged()
            )
            .subscribe(() => this.loadData());
        const reportLoadedSub = this.reportsService.onReportIdLoaded.subscribe(rId => this.openSelectedGroup(rId));

        this.loadData();
        this.addSubscriptions(routerSub, reportLoadedSub);
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    hasChild(_: number, node: TreeNodeModel): boolean {
        return node.isExpandable;
    }

    viewReport(id: string): void {
        this.router.navigate(['reports', id]);
    }

    editReport(id: string): void {
        this.router.navigate(['reports', id, 'edit']);
    }

    /**
     * The currentResponse needs to be cleared, since this is not a move to a completely new page,
     * but just to a subpage (/reports/{id1} -> /reports/{id2})
     */
    clearCurrentResponse(): void {
        this.controllerService.currentResponse = undefined;
    }

    private loadData(): void {
        this.reportsResource.getAll().subscribe(r => {
            if (location.href.endsWith('reports')) {
                speedDialButtons.set(this.actionButtonsService.convert(r.relatedActions));
            }

            this.setupTree(r.value);
        });
    }

    private setupTree(items: ReportItemModel[]): void {
        const names = items.map(r => r.group);
        const distinctGroupNames = [...new Set(names)].sort();

        const newTree: ReportTreeItemModel[] = [];

        distinctGroupNames.forEach(n => newTree.push(new ReportTreeItemModel({ name: n })));
        items.forEach(item => {
            const group = newTree.find(g => g.name === item.group)!;
            group.children.push(
                new ReportTreeItemModel({
                    id: item.id.toString(),
                    name: item.description,
                    hasSchedule: item.hasSchedule
                })
            );
        });
        items.forEach(item => {
            const group = newTree.find(g => g.name === item.group)!;
            group.children.sort((a, b) => (a.name > b.name ? 1 : -1));
        });

        this.tree.set(newTree);
    }

    private openSelectedGroup(reportId: string): void {
        let newGroup = '';

        for (const tree of this.tree()) {
            for (const leaf of tree.children) {
                // We use double-equals because the types might be different (number/string).
                // eslint-disable-next-line eqeqeq
                if (leaf.id == reportId) {
                    newGroup = tree.name;
                    break;
                }
            }

            if (!!newGroup) {
                break;
            }
        }

        this.selectedGroup.set(newGroup);
    }
}
